import request from "../helpers/request";

/** Kabinet xidmət açarı → company.functions dəyəri */
export const CABINET_SERVICE_FUNCTIONS = {
  tests: ["test"],
  pastTests: ["test_history"],
  reports: ["report"],
  diagnostics: ["diagnostic"],
  appointment: ["appointment"],
  materials: ["material"],
  supportVideos: ["video"],
  trainings: ["training", "trainings"],
  webinar: ["webinar", "webinars", "seminar", "online_seminar", "onlineseminar", "vebinar"],
};

function normalizeFunctionName(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/** Profil cavabından company.functions siyahısını çıxarır */
export function getCompanyFunctions(profile) {
  const raw =
    profile?.company?.functions ??
    profile?.functions ??
    profile?.company_functions ??
    [];

  if (!Array.isArray(raw)) return [];

  return raw.map(normalizeFunctionName).filter(Boolean);
}

/**
 * Profil cavabından kabinet xidmətinin görünürlüyünü yoxlayır.
 * Görünürlük yalnız company.functions massivinə əsaslanır.
 */
export function isCabinetServiceVisible(profile, serviceKey) {
  const aliases = CABINET_SERVICE_FUNCTIONS[serviceKey];
  if (!aliases?.length) return false;

  const functions = getCompanyFunctions(profile);
  if (!functions.length) return false;

  return aliases.some((alias) => functions.includes(normalizeFunctionName(alias)));
}

/**
 * Profil sidebar tabının görünürlüyünü yoxlayır.
 * functionKey yoxdursa tab həmişə görünür.
 */
export function isProfileSidebarTabVisible(profile, functionKey) {
  if (!functionKey) return true;
  return isCabinetServiceVisible(profile, functionKey);
}

/**
 * Sidebar tab siyahısını profil görünürlüyünə görə filtrləyir.
 * Hər elementdə `functionKey` (opsional) olmalıdır.
 */
export function filterVisibleProfileSidebarTabs(tabItems, profile) {
  if (!Array.isArray(tabItems)) return [];
  return tabItems.filter((item) => isProfileSidebarTabVisible(profile, item.functionKey));
}

/** Xidmətlər bölməsində sabit göstərilmə sırası */
export const CABINET_SERVICES_DISPLAY_ORDER = [
  "diagnostics",
  "materials",
  "supportVideos",
  "tests",
  "pastTests",
  "trainings",
  "reports",
  "webinar",
];

/**
 * Xidmət siyahısını profil görünürlüyünə görə filtrləyir.
 * Hər elementdə `key` olmalıdır (CABINET_SERVICE_FUNCTIONS açarları).
 */
export function filterVisibleCabinetServices(serviceItems, profile) {
  if (!Array.isArray(serviceItems)) return [];

  const orderMap = new Map(
    CABINET_SERVICES_DISPLAY_ORDER.map((key, index) => [key, index])
  );

  return serviceItems
    .filter((item) => isCabinetServiceVisible(profile, item.key))
    .sort((a, b) => {
      const orderA = orderMap.get(a.key) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(b.key) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
}

/**
 * GET /api/cabinet/profile/me cavabını normalizə edir.
 */
export function normalizeProfileMe(payload) {
  const raw = payload?.data ?? payload;
  if (!raw || typeof raw !== "object") return null;
  return raw;
}

const API_ORIGIN = String(
  process.env.REACT_APP_API_URL || "https://dev-api.lifechart.az"
).replace(/\/$/, "");

function resolveDocumentFileUrl(value) {
  if (value == null) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/")) return `${API_ORIGIN}${trimmed}`;
    return `${API_ORIGIN}/${trimmed.replace(/^\//, "")}`;
  }

  if (typeof value === "object") {
    return resolveDocumentFileUrl(
      value.file_url ??
        value.fileUrl ??
        value.url ??
        value.path ??
        value.link ??
        value.href ??
        value.file
    );
  }

  return "";
}

function pickDocumentField(item, ...names) {
  if (!item || typeof item !== "object") return undefined;
  for (const name of names) {
    if (item[name] != null && item[name] !== "") return item[name];
    const foundKey = Object.keys(item).find(
      (key) => key.toLowerCase() === String(name).toLowerCase()
    );
    if (foundKey && item[foundKey] != null && item[foundKey] !== "") {
      return item[foundKey];
    }
  }
  return undefined;
}

export function normalizeCompanyDocument(item) {
  if (!item || typeof item !== "object") return null;

  const fileUrl = resolveDocumentFileUrl(
    pickDocumentField(
      item,
      "file_url",
      "fileUrl",
      "document_url",
      "documentUrl",
      "url",
      "path",
      "file",
      "download_url"
    ) ??
      item.document?.file_url ??
      item.document?.url
  );

  const title = String(
    pickDocumentField(item, "name", "title", "document_name", "file_name") || ""
  ).trim();

  if (!title && !fileUrl) return null;

  return {
    id: item.id ?? item.document_id ?? fileUrl ?? title,
    title: title || "—",
    fileUrl,
    createdAt: pickDocumentField(item, "created_at", "createdAt", "date", "uploaded_at") || "",
    raw: item,
  };
}

/** Profil cavabından company.documents siyahısını çıxarır */
export function getCompanyDocuments(profile) {
  const raw =
    profile?.company?.documents ??
    profile?.documents ??
    profile?.company_documents ??
    [];

  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeCompanyDocument).filter(Boolean);
}

export function hasCompanyDocuments(profile) {
  return getCompanyDocuments(profile).length > 0;
}

const COMPANY_DOCUMENTS_ACK_KEY = "lifechart.cabinetDocumentsAck";

function getProfileAckId(profile) {
  return String(
    profile?.id ??
      profile?.user_id ??
      profile?.email ??
      profile?.company?.id ??
      ""
  );
}

export function getCompanyDocumentsFingerprint(profile) {
  return getCompanyDocuments(profile)
    .map((doc) => String(doc.id))
    .sort()
    .join("|");
}

export function hasAcknowledgedCompanyDocuments(profile) {
  if (!hasCompanyDocuments(profile)) return true;

  try {
    const raw = sessionStorage.getItem(COMPANY_DOCUMENTS_ACK_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return (
      parsed?.userId === getProfileAckId(profile) &&
      parsed?.fingerprint === getCompanyDocumentsFingerprint(profile)
    );
  } catch {
    return false;
  }
}

export function acknowledgeCompanyDocuments(profile) {
  sessionStorage.setItem(
    COMPANY_DOCUMENTS_ACK_KEY,
    JSON.stringify({
      userId: getProfileAckId(profile),
      fingerprint: getCompanyDocumentsFingerprint(profile),
    })
  );
}

export function clearCompanyDocumentsAck() {
  try {
    sessionStorage.removeItem(COMPANY_DOCUMENTS_ACK_KEY);
  } catch {
    // ignore storage errors
  }
}

/** profile/me `agreement_accepted`: tarix və ya null (qəbul edilməyib). */
export function getAgreementAcceptedAt(profile) {
  if (!profile || typeof profile !== "object") return null;
  const value = profile.agreement_accepted ?? profile.raw?.agreement_accepted ?? null;
  if (value == null || String(value).trim() === "") return null;
  return value;
}

function asAgreementText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    const localized =
      value.az ?? value.en ?? value.ru ?? value.text ?? value.content ?? value.body ?? value.html;
    if (typeof localized === "string" || typeof localized === "number") return String(localized);
  }
  return "";
}

function firstAgreementText(source, keys) {
  if (!source || typeof source !== "object") return "";
  for (const key of keys) {
    const text = asAgreementText(source[key]).trim();
    if (text) return text;
  }
  return "";
}

function looksLikeHtml(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

function normalizeAgreementSection(item, index) {
  if (typeof item === "string" || typeof item === "number") {
    const body = String(item).trim();
    if (!body) return null;
    return { id: index, title: "", body, html: looksLikeHtml(body) };
  }
  if (!item || typeof item !== "object") return null;
  const title = firstAgreementText(item, ["title", "name", "heading"]);
  const body = firstAgreementText(item, ["body", "content", "text", "description", "html"]);
  if (!title && !body) return null;
  return { id: item.id ?? index, title, body, html: looksLikeHtml(body) };
}

/** GET /api/cabinet/agreement və /api/specialist-cabinet/agreement cavabı. */
export function normalizeAgreement(payload) {
  const raw = payload?.data !== undefined ? payload.data : payload;
  if (raw == null) return { title: "", content: "", html: false, sections: [] };

  if (typeof raw === "string" || typeof raw === "number") {
    const content = String(raw);
    return { title: "", content, html: looksLikeHtml(content), sections: [] };
  }

  if (Array.isArray(raw)) {
    return {
      title: "",
      content: "",
      html: false,
      sections: raw.map(normalizeAgreementSection).filter(Boolean),
    };
  }

  if (typeof raw !== "object") {
    return { title: "", content: "", html: false, sections: [] };
  }

  const nested = raw.agreement && typeof raw.agreement === "object" ? raw.agreement : raw;
  const sectionsSource = ["sections", "items", "rules", "points"].reduce((found, key) => {
    if (found) return found;
    return Array.isArray(nested[key]) ? nested[key] : null;
  }, null);

  const content = firstAgreementText(nested, [
    "content",
    "body",
    "text",
    "description",
    "html",
    "rules",
    "agreement",
  ]);

  return {
    title: firstAgreementText(nested, ["title", "name", "heading"]),
    content,
    html: looksLikeHtml(content),
    sections: (sectionsSource || []).map(normalizeAgreementSection).filter(Boolean),
  };
}

export function agreementHasBody(agreement) {
  if (!agreement) return false;
  if (String(agreement.content || "").trim()) return true;
  return (
    Array.isArray(agreement.sections) &&
    agreement.sections.some((section) => section?.title || section?.body)
  );
}

export function extractAgreementAcceptedAt(payload) {
  if (payload == null) return null;
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  if (typeof payload !== "object") return null;

  const direct = payload.agreement_accepted;
  if (direct != null && String(direct).trim() !== "") return direct;

  const data = payload.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const nested = data.agreement_accepted ?? data.accepted_at ?? data.acceptedAt ?? null;
    if (nested != null && String(nested).trim() !== "") return nested;
  }
  return null;
}

/** Qaydalar: GET /api/cabinet/agreement */
export const getCabinetAgreement = async () => {
  const response = await request.get("/api/cabinet/agreement");
  return normalizeAgreement(response.data);
};

/** Qaydaları qəbul et: POST /api/cabinet/agreement/accept */
export const acceptCabinetAgreement = async () => {
  const response = await request.post("/api/cabinet/agreement/accept", {});
  return response.data;
};
